import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import verificationService from '../../services/verificationService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import './AdminVerificationPage.css';

const AdminVerificationPage = () => {
    const { t } = useTranslation();
    const { token } = useAuth();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'rejected'

    const fetchRequests = useCallback(async () => {
        try {
            setLoading(true);
            let data = [];
            if (activeTab === 'pending') {
                data = await verificationService.getPendingRequests(token);
            } else {
                data = await verificationService.getRejectedRequests(token);
            }
            setRequests(data);
        } catch (error) {
            console.error(error);
            toast.error('Error fetching requests');
        } finally {
            setLoading(false);
        }
    }, [token, activeTab]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const handleApprove = async (id) => {
        if (!window.confirm(t('confirm_approve') || 'Are you sure you want to approve this artist?')) return;
        try {
            await verificationService.approveRequest(token, id);
            toast.success(t('artist_approved_success') || 'Artist approved successfully!');
            fetchRequests(); // Refresh list
        } catch (error) {
            console.error(error);
            toast.error('Error approving request');
        }
    };

    const handleReject = async (id) => {
        const reason = window.prompt(t('enter_rejection_reason') || 'Please enter the reason for rejection:');
        if (reason === null) return; // Cancelled

        try {
            await verificationService.rejectRequest(token, id, reason);
            toast.success(t('request_rejected_success') || 'Request rejected.');
            fetchRequests();
        } catch (error) {
            console.error(error);
            toast.error('Error rejecting request');
        }
    };

    const handleResetCooldown = async (id) => {
        if (!window.confirm(t('confirm_reset', 'Deseja zerar o cooldown e permitir nova tentativa?'))) return;
        try {
            await verificationService.resetCooldown(token, id);
            toast.success(t('cooldown_reset_success') || 'Cooldown reset successfully.');
            fetchRequests();
        } catch (error) {
            console.error(error);
            toast.error('Error resetting cooldown');
        }
    };

    return (
        <div className="admin-verification-page">
            <div className="container">
                <h1>{t('artist_verification_requests') || 'Artist Verification Requests'}</h1>

                <div className="verification-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                        onClick={() => setActiveTab('pending')}
                    >
                        {t('verification_requests', 'Pendentes')}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'rejected' ? 'active' : ''}`}
                        onClick={() => setActiveTab('rejected')}
                    >
                        {t('rejected_requests_tab', 'Recusadas')}
                    </button>
                </div>

                {loading ? (
                    <p>{t('loading')}...</p>
                ) : requests.length === 0 ? (
                    <p className="no-requests">
                        {activeTab === 'pending'
                            ? (t('no_pending_requests') || 'No pending requests.')
                            : (t('no_rejected_requests') || 'No rejected requests.')
                        }
                    </p>
                ) : (
                    <div className="requests-table-container">
                        <table className="requests-table">
                            <thead>
                                <tr>
                                    <th>{t('user')}</th>
                                    <th>{t('instagram')}</th>
                                    <th>{t('portfolio')}</th>
                                    <th>{t('message', 'Mensagem')}</th>
                                    <th>{t('date')}</th>
                                    <th>{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map(req => (
                                    <tr key={req.id} className={req.isPriority ? 'priority-row' : ''}>
                                        <td data-label={t('user')}>
                                            <div className="user-info">
                                                <span>{req.user?.username}</span>
                                            </div>
                                        </td>
                                        <td data-label={t('instagram')}>
                                            <a
                                                href={`https://instagram.com/${req.instagramHandle.replace('@', '')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="instagram-link"
                                            >
                                                {req.instagramHandle} ↗
                                            </a>
                                        </td>
                                        <td data-label={t('portfolio')}>
                                            {req.portfolioUrl ? (
                                                <a href={req.portfolioUrl} target="_blank" rel="noopener noreferrer">
                                                    {t('view_portfolio') || 'View Portfolio'} ↗
                                                </a>
                                            ) : '-'}
                                        </td>
                                        <td data-label={t('message', 'Mensagem')}>
                                            <div className="request-message-cell">
                                                {req.userMessage || '-'}
                                            </div>
                                        </td>
                                        <td data-label={t('date')}>
                                            <div className="request-date-cell">
                                                {new Date(req.createdAt).toLocaleDateString()}
                                                {req.isPriority && <span className="priority-badge" title={t('priority_request_desc', 'Pedido com Convite')}>🔥 {t('priority')}</span>}
                                            </div>
                                        </td>
                                        <td data-label={t('actions')}>
                                            <div className="action-buttons">
                                                {activeTab === 'pending' ? (
                                                    <>
                                                        <button
                                                            className="btn-approve"
                                                            onClick={() => handleApprove(req.id)}
                                                        >
                                                            {t('approve')}
                                                        </button>
                                                        <button
                                                            className="btn-reject"
                                                            onClick={() => handleReject(req.id)}
                                                        >
                                                            {t('reject')}
                                                        </button>
                                                    </>
                                                ) : (
                                                    (() => {
                                                        const cooldownDays = 60;
                                                        const now = new Date();
                                                        const updatedAt = new Date(req.updatedAt);
                                                        const diffTime = Math.abs(now - updatedAt);
                                                        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                                        const stillInCooldown = diffDays < cooldownDays;

                                                        if (!stillInCooldown) return <span className="status-permitted">✅ {t('already_permitted', 'Acesso Liberado')}</span>;

                                                        return (
                                                            <button
                                                                className="btn-reset"
                                                                onClick={() => handleResetCooldown(req.id)}
                                                                title="Permitir que o usuário tente novamente agora"
                                                            >
                                                                {t('reset_cooldown', 'Permitir Nova Solicitação')}
                                                            </button>
                                                        );
                                                    })()
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div >
    );
};

export default AdminVerificationPage;
